import React, { useCallback, useMemo, useState } from 'react';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useApplyReopenDates } from '~/hooks/apis/reopen/useApplyReopenDates';
import { useExtractReopenDates } from '~/hooks/apis/reopen/useExtractReopenDates';
import { useBlackoutDatesByRoomTypes } from '~/hooks/apis/stopsell/useBlackoutDatesByRoomTypes';

import { ReopenExtraction } from '~/types/reopen';
import { CatalogRoom, ResolvedRoomMatch, StopsellApplyPayload } from '~/types/stopsell';

import AvailabilityDocumentUploadZone from './AvailabilityDocumentUploadZone';
import AvailabilityRateSelectModal from './AvailabilityRateSelectModal';
import AvailabilityRoomMatchModal from './AvailabilityRoomMatchModal';
import ReopenReviewModal from './ReopenReviewModal';

interface Props {
  propertyId: string;
  roomTypes: Array<App.RoomType>;
  hideHeader?: boolean;
}

export default function ReopenManagementPane({ propertyId, roomTypes, hideHeader = false }: Props) {
  const { mutate, isPending, error } = useExtractReopenDates();
  const { mutate: applyReopenDates, isPending: isApplying } = useApplyReopenDates(propertyId);
  const [extractionResult, setExtractionResult] = useState<ReopenExtraction | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [matchOpen, setMatchOpen] = useState(false);
  const [rateSelectOpen, setRateSelectOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedRateIds, setSelectedRateIds] = useState<Record<string, Array<string>> | null>(null);

  const {
    isLoading: isBlackoutLoading,
    isError: isBlackoutError,
    byRoomType: blackoutsByRoomType,
  } = useBlackoutDatesByRoomTypes(propertyId, roomTypes);

  const catalogRooms = useMemo<Array<CatalogRoom>>(
    () =>
      roomTypes.map((r) => ({
        id: r.id,
        name: r.name,
        roomRates: (r.room_rates ?? []).map((rr) => ({ id: rr.id, name: rr.rate_plan.name })),
        blackoutDates: blackoutsByRoomType[r.id] ?? [],
      })),
    [roomTypes, blackoutsByRoomType],
  );

  const catalogRoomsForReview = useMemo<Array<CatalogRoom>>(() => {
    if (!selectedRateIds) return catalogRooms;
    return catalogRooms.map((room) => {
      const rateSet = new Set(selectedRateIds[room.id] ?? []);
      return {
        ...room,
        blackoutDates: (room.blackoutDates ?? []).map((bd) => ({
          ...bd,
          blocks: bd.blocks.filter((b) => rateSet.has(b.room_rate_id)),
        })),
      };
    });
  }, [catalogRooms, selectedRateIds]);

  const handleSelectFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      mutate(
        { file, catalogRooms },
        {
          onSuccess: (data: ReopenExtraction) => {
            setExtractionResult(data);
            setMatchOpen(true);
          },
        },
      );
    },
    [mutate, catalogRooms],
  );

  const handleMatchContinue = useCallback(
    (resolvedRooms: Array<ResolvedRoomMatch>) => {
      if (!extractionResult) return;
      const resolved: ReopenExtraction = {
        rooms: extractionResult.rooms.map((room, index) => ({
          ...room,
          roomTypeId: resolvedRooms[index]?.catalogRoomId ?? room.roomTypeId,
        })),
      };
      setExtractionResult(resolved);
      setMatchOpen(false);
      setRateSelectOpen(true);
    },
    [extractionResult],
  );

  const handleCloseMatch = useCallback(() => setMatchOpen(false), []);

  const handleRateSelectContinue = useCallback((rates: Record<string, Array<string>>) => {
    setSelectedRateIds(rates);
    setRateSelectOpen(false);
    setReviewOpen(true);
  }, []);

  const handleCloseRateSelect = useCallback(() => setRateSelectOpen(false), []);

  const handleCloseReview = useCallback(() => setReviewOpen(false), []);

  const handleApply = useCallback(
    (payload: StopsellApplyPayload) => {
      applyReopenDates(payload, { onSuccess: handleCloseReview });
    },
    [applyReopenDates, handleCloseReview],
  );

  return (
    <>
      {!hideHeader && <PageSubheader title="Re-open Management" />}
      <AvailabilityDocumentUploadZone
        onSelectFile={handleSelectFile}
        isPending={isPending}
        error={error?.message}
        title="Upload re-open document"
      />

      {extractionResult && (
        <>
          <AvailabilityRoomMatchModal
            key={fileName}
            open={matchOpen}
            onClose={handleCloseMatch}
            onContinue={handleMatchContinue}
            extraction={extractionResult}
            catalogRooms={catalogRooms}
            fileName={fileName}
            isBlackoutLoading={isBlackoutLoading}
            workflowLabel="applying re-open dates"
          />

          <AvailabilityRateSelectModal
            open={rateSelectOpen}
            onClose={handleCloseRateSelect}
            onContinue={handleRateSelectContinue}
            catalogRooms={catalogRooms}
            fileName={fileName}
            description="Choose which room rates to re-open. This applies to both extracted and manually added rooms."
          />

          <ReopenReviewModal
            open={reviewOpen}
            onClose={handleCloseReview}
            onApply={handleApply}
            extraction={extractionResult}
            fileName={fileName}
            catalogRooms={catalogRoomsForReview}
            isBlackoutError={isBlackoutError}
            isApplying={isApplying}
          />
        </>
      )}
    </>
  );
}
